import { useEffect, useState } from "react";
import { OBMarketingAnswerModel, OnboardingProfile } from "@models/Models";
import ParticlesBackground from "@components/common/ParticlesBackground";
import { MxResult, OBPersonaAnswerKey } from "@models/Enums";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { kAnalyticsConstants } from "@utils/constants/AnalyticsConstants";
import { recordEvent } from "@utils/managers/AnalyticsManager";
import MarketingRow from "../Marketing/MarketingRow";
import { postOnboardingMarketingAnswer, postOnboardingPersonaAnswers } from "@utils/managers/networking/NetworkManager";
import OnboardingGoalModal from "@components/common/OnboardingGoalModal";
import { useOnboardingProfile } from "@providers/OnboardingContext";

const baseMarketingAnswers: OBMarketingAnswerModel[] = [
	{
		emoji: "📥",
		title: "Have all my newsletters in one place",
		key: OBPersonaAnswerKey.onePlace,
	},
	{
		emoji: "🧹",
		title: "Keep my personal inbox clean",
		key: OBPersonaAnswerKey.keepInboxClean,
	},
	{
		emoji: "👓",
		title: "Upgrade reading experience",
		key: OBPersonaAnswerKey.betterReading,
	},
	{
		emoji: "✨",
		title: "Organise my newsletters",
		key: OBPersonaAnswerKey.organiseNewsletters,
	},
];

const Persona = (props) => {

	const shuffleArray = (array: OBMarketingAnswerModel[]): OBMarketingAnswerModel[] => {
		const newArray = [...array];
		for (let i = newArray.length - 1; i > 0; i--) {
			const j = Math.floor(Math.random() * (i + 1));
			[newArray[i], newArray[j]] = [newArray[j], newArray[i]];
		}
		newArray.push({ emoji: "🫢", title: "Something else", key: OBPersonaAnswerKey.somethingElse });
		return newArray;
	};
	
	const [selectedAnswers, setSelectedAnswers] = useState<string[]>([]);
	const location = useLocation();
	const mxResult: string | undefined = location.state?.mxResult;
	const [isModalShow, setIsModalShow] = useState<boolean>(false);
	const [marketingAnswers] = useState(() => shuffleArray(baseMarketingAnswers));
	const navigate = useNavigate();
	const { onboardingProfile, setOnboardingProfile } = useOnboardingProfile();

	useEffect(() => {
		recordEvent(kAnalyticsConstants.Onboarding.personaAnswerPresented);
		const onboardingProfile = new OnboardingProfile();
		onboardingProfile.persistence.mxResult = mxResult;
		setOnboardingProfile(onboardingProfile);
	}, []);

	const onAnswerClick = (answer: OBMarketingAnswerModel) => {
		if (selectedAnswers.includes(answer.key)) {
			setSelectedAnswers(selectedAnswers.filter((key) => key !== answer.key));
		} else {
			setSelectedAnswers([...selectedAnswers, answer.key]);
		}
	};

	const goToNextScreen = () => {
		var tempOnboardingProfile = { ...onboardingProfile! };
		tempOnboardingProfile.persistence.answersList = marketingAnswers;
		tempOnboardingProfile.persistence.personaAnswers = selectedAnswers;

		if (selectedAnswers.length > 1) {
			setOnboardingProfile(tempOnboardingProfile);
			navigate("/get-started/priority-goal");
			return;
		} 

		tempOnboardingProfile.persistence.priorityAnswer = selectedAnswers[0];
		setOnboardingProfile(tempOnboardingProfile);
		recordEvent(kAnalyticsConstants.Onboarding.priorirtyAnswerAnswered, { question_key: "segment_question", selected_answers: selectedAnswers, priority_answer: selectedAnswers[0] });
		postOnboardingPersonaAnswers("segment_question", selectedAnswers, selectedAnswers[0]);
		setIsModalShow(true);
	};

	return (
		<div>
			<ParticlesBackground />
			<OnboardingGoalModal isShow={isModalShow} setIsShow={() => setIsModalShow(false)} selectedAnswer={selectedAnswers[0]} />
			<div className={`flex min-h-[100dvh] flex-col md:justify-center justify-start py-2 pointer-events-auto`}>
				<div className="fixed top-0 bg-black/50 w-full p-3 z-30 backdrop-blur-lg border-b border-white/10 shadow-2xl">
					<div className="max-w-[1080px] w-full z-30 m-auto">
						<div className="flex justify-end items-center">
							<button className={`text-white font-medium font-primary text-lg focus:outline-none rounded-xl p-2 px-3 py-2 items-center bg-success-green/80 hover:bg-success-green/100 transition ease-in-out whitespace-nowrap ${selectedAnswers.length === 0 ? "opacity-50 cursor-not-allowed" : ""}`} onClick={goToNextScreen} disabled={selectedAnswers.length === 0}>Next</button>
						</div>
					</div>
				</div>
				<div className="flex flex-col gap-10 items-center justify-center p-3 mt-[50px] md:mt-[80px]">
					<div className="meco_container max-w-[1080px] py-[40px] md:p-[60px] md:pb-[40px]">
						<div className="flex flex-col md:flex-row gap-5 w-full justify-between">
							<div className="sticky md:top-[90px] w-full md:w-1/2 flex flex-col gap-2">
								<div className="text-white font-bold font-primary text-4xl leading-10">With Meco I want to...</div>
								<div className="text-force-primary-light font-medium font-primary text-base  md:text-xl">Choose all that apply</div>
							</div>
							<div className="w-full md:w-1/2 flex flex-col items-center justify-center gap-3 max-w-[450px] md:max-w-none m-auto md:min-w-[400px]">
								<div className="text-white font-medium font-primary text-3xl text-center w-full max-w-[600px] mb-5  no-select">
									{marketingAnswers.map((answer, i) => {
										return <MarketingRow marketingAnswer={answer} isSelected={selectedAnswers.includes(answer.key)} onSelect={onAnswerClick} />;
									})}
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default Persona;
